import { api } from "shared/redux/api"
import { camelizeKeys } from "shared/utils/camelize"

const BASE = "/public/vendor-proposals/v1"

const unwrap = (response) => camelizeKeys(response?.data)

// Public, no-auth endpoints — a supplier opens a shared link and submits a
// price proposal for an Order Request without ever logging in.
const publicVendorProposalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // `supplierId` is what scopes the response's RFQ-context fields (title/
    // terms/requiredDocuments/deliveryLocation/deliveryDate/noteForVendor/
    // contact*) to this one supplier's own vendor_proposal row — omitting it
    // still returns the order's lines but every rfq* field comes back null.
    // Also `requirements[]` { criteriaId, name, orderNo, answer } — the OR's
    // shared requirement set ([] if none); `answer` is this supplier's saved one.
    getVendorProposalOrderLines: build.query({
      query: ({ orderRequestId, supplierId }) => ({
        url: `${BASE}/order-requests/${orderRequestId}/lines`,
        params: supplierId ? { supplierId } : undefined,
      }),
      transformResponse: unwrap,
    }),
    // GET /purchases/{purchaseId}/lines — same idea as
    // getVendorProposalOrderLines but for a Purchase's own vendor proposals.
    // If the PO originated from a group order, `lines` is already combined
    // server-side (summed by itemId across every member order) — catalog
    // lines have no `lineId`; each off-catalog line is its own row with one.
    getVendorProposalPurchaseLines: build.query({
      query: ({ purchaseId, supplierId }) => ({
        url: `${BASE}/purchases/${purchaseId}/lines`,
        params: supplierId ? { supplierId } : undefined,
      }),
      transformResponse: unwrap,
    }),
    // GET /group-orders/{id}/lines — a Group Order RFQ link
    // (`?groupId=&supplierId=&pin=`). Same shape as the order variant:
    // catalog items summed per itemId (no lineId), each off-catalog line on
    // its own row with a lineId.
    getVendorProposalGroupLines: build.query({
      query: ({ groupOrderId, supplierId }) => ({
        url: `${BASE}/group-orders/${groupOrderId}/lines`,
        params: supplierId ? { supplierId } : undefined,
      }),
      transformResponse: unwrap,
    }),
    // Response's `proposal` now also carries `supplier` (full object) and
    // `expiredAt`. New failure mode: 410 Gone when `pin` is correct but
    // `expiredAt` has already passed (403 stays reserved for a wrong/reused pin).
    // Exactly one of `orderRequestId`/`purchaseId` is set, matching whichever
    // "lines" query the page loaded from (groupOrderId isn't supported here
    // yet — a group-order proposal is submitted against its resulting PO's
    // purchaseId once one exists, or against a member order directly).
    submitVendorProposal: build.mutation({
      query: ({
        orderRequestId,
        groupOrderId,
        purchaseId,
        supplierId,
        pin,
        proposals,
        extraNote,
        deliveryFee,
        taxCode,
        vendorInfo,
        requirementAnswers,
        files,
      }) => {
        const formData = new FormData()
        formData.append(
          "payload",
          new Blob(
            [
              JSON.stringify({
                // Exactly one of orderRequestId / groupOrderId / purchaseId.
                orderRequestId: orderRequestId || undefined,
                groupOrderId: groupOrderId || undefined,
                purchaseId: purchaseId || undefined,
                supplierId,
                pin: pin || undefined,
                // B2 — proposal-level extra note (BE: vendor_extra_note).
                extraNote: extraNote || undefined,
                // Shipping fee for the whole quote — shown only, never added
                // to the PO total. Omitted = keep the previous value.
                deliveryFee: deliveryFee ?? undefined,
                // Vendor's tax code as quoted on this proposal (≤ 20 chars) — comes
                // back as proposalTaxCode; supplier.taxCode stays the master value.
                taxCode: taxCode || undefined,
                // address (≤ 500, also overwrites supplier.address), alternative,
                // extraInfo, contactName (≤ 200), contactEmail (≤ 100, valid
                // email), contactPhone (≤ 30). Blank/omitted keeps the saved value.
                address: vendorInfo?.address || undefined,
                alternative: vendorInfo?.alternative || undefined,
                extraInfo: vendorInfo?.extraInfo || undefined,
                contactName: vendorInfo?.contactName || undefined,
                contactEmail: vendorInfo?.contactEmail || undefined,
                contactPhone: vendorInfo?.contactPhone || undefined,
                // OR/PO only (group → 400): { [criteriaId]: text }, "" clears,
                // omitted id keeps the saved answer. An id outside this
                // OR/PO's `requirements` → 400 and the whole submit rolls back.
                requirementAnswers:
                  requirementAnswers && Object.keys(requirementAnswers).length ? requirementAnswers : undefined,
                // Exactly one of itemId (catalog) / orderRequestLineId
                // (off-catalog line) per row, else 400.
                proposals: (proposals || []).map((p) => ({
                  itemId: p.itemId ?? undefined,
                  orderRequestLineId: p.orderRequestLineId ?? undefined,
                  proposedAmount: p.proposedAmount,
                  note: p.note || undefined,
                  // B1 — alternative goods per line.
                  // TODO(BE): accept `has_alternative` / `alternative_note` on
                  // each proposal line.
                  hasAlternative: p.hasAlternative || undefined,
                  alternativeNote: p.alternativeNote || undefined,
                  // Omitted = BE keeps the saved value; negative months → 400.
                  warrantyMonths: p.warrantyMonths ?? undefined,
                  warrantyNote: p.warrantyNote || undefined,
                })),
              }),
            ],
            { type: "application/json" }
          )
        )
        ;(files || []).forEach((file) => formData.append("files", file))

        return {
          url: `${BASE}/submit`,
          method: "POST",
          body: formData,
        }
      },
      transformResponse: unwrap,
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetVendorProposalOrderLinesQuery,
  useGetVendorProposalPurchaseLinesQuery,
  useGetVendorProposalGroupLinesQuery,
  useSubmitVendorProposalMutation,
} = publicVendorProposalsApi
